// The project's immovable goals (Brief 4 / P8) — the alignment anchor at the
// top of a project's sessions. This holds the goals/spec/rules the orchestrator
// establishes for the project: the highest-level context, kept clean and pruned,
// that the orchestrator keeps every session aligned to from the Operators view.
// (Authoring + reading is built here; wiring these goals INTO the Operators'
// judge context — so drift off them is flagged automatically — is the next
// step.) Read as rendered markdown (react-markdown emits no raw HTML, so the
// document can never inject markup), authored inline. Collapsible so it frames
// the sessions without crowding them.

import { useEffect, useState } from "react";
import Markdown from "react-markdown";
import { useProjectGoals } from "./useProjectGoals";

export function ProjectGoals({
  projectId,
  defaultOpen = true,
}: {
  projectId: number;
  defaultOpen?: boolean;
}) {
  const { markdown, loading, saving, error, save } = useProjectGoals(projectId);
  const [open, setOpen] = useState(defaultOpen);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  // Keep the draft in step with the loaded document while NOT editing, so
  // entering edit starts from the current goals.
  useEffect(() => {
    if (!editing) setDraft(markdown);
  }, [markdown, editing]);

  const empty = !loading && markdown.trim() === "";

  async function onSave() {
    try {
      await save(draft);
      setEditing(false);
    } catch {
      // the error is surfaced by the hook; stay in edit so the draft is kept
    }
  }

  return (
    <section className="wt-goals" data-testid="project-goals">
      <header className="wt-goals__head">
        <button
          type="button"
          className="wt-goals__toggle"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
        >
          <span aria-hidden="true">{open ? "▾" : "▸"}</span> Immovable goals
        </button>
        {!editing ? (
          <button
            type="button"
            className="wt-goals__edit"
            onClick={() => {
              setDraft(markdown);
              setEditing(true);
              setOpen(true);
            }}
          >
            {empty ? "Set goals" : "Edit"}
          </button>
        ) : null}
      </header>

      {open ? (
        <div className="wt-goals__body">
          {error ? <p className="wt-goals__err">{error}</p> : null}
          {loading ? (
            <p className="wt-dash-empty">Loading goals…</p>
          ) : editing ? (
            <div className="wt-goals__editor">
              <textarea
                className="wt-goals__textarea"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="The goals, spec, and rules every session in this project must hold to. Markdown — keep it clean and pruned."
                aria-label="Immovable goals (markdown)"
                rows={10}
              />
              <div className="wt-goals__actions">
                <button
                  type="button"
                  className="wt-goals__save"
                  onClick={() => void onSave()}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save goals"}
                </button>
                <button
                  type="button"
                  className="wt-goals__cancel"
                  onClick={() => {
                    setEditing(false);
                    setDraft(markdown);
                  }}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : empty ? (
            <p className="wt-dash-empty">
              No immovable goals yet. Set the goals, spec, and rules for this
              project — the highest-level context you keep every session aligned
              to from the Operators view.
            </p>
          ) : (
            <div className="wt-goals__doc">
              <Markdown>{markdown}</Markdown>
            </div>
          )}
        </div>
      ) : null}
    </section>
  );
}
